import React, { useState } from 'react';
import Viz from '../Viz';
import { outputResultOf } from './graphModel';
import { pickFields } from './flowFields';
import { IMAGE_CHART_TYPES, renderExportCanvas, slugify } from '../export/chartExporter';
import { downloadCanvasAsPng } from '../downloadExport';

// One output node: its chart or table, the status of the last run and the
// export buttons for exactly those rows.
export default function ResultCard({ node, meta, result, onExport, background }) {
  const [imageError, setImageError] = useState(null);
  const nodeResult = outputResultOf(result, node.id);
  const params = node.params || {};
  const title = params.title || (meta && meta.name) || node.type;
  const failed = nodeResult && nodeResult.status === 'failed';
  const rows = (nodeResult && nodeResult.rows) || [];
  const fields = pickFields(nodeResult && nodeResult.fields, params.columns);
  const chartType = params.chartType || 'table';
  const canImage =
    node.type === 'chart' && IMAGE_CHART_TYPES.has(chartType) && rows.length > 0 && !failed;

  return (
    <article className="flow-result-card">
      <header className="flow-result-head">
        <h3 className="flow-result-title">{title}</h3>
        <span
          className={failed ? 'flow-result-status flow-result-status-failed' : 'flow-result-status'}
        >
          {statusText(nodeResult, failed)}
        </span>
        <span className="flow-result-export">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!rows.length}
            onClick={() => onExport(node.id, 'csv')}
          >
            CSV
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!rows.length}
            onClick={() => onExport(node.id, 'xlsx')}
          >
            XLSX
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!canImage}
            onClick={downloadImage}
          >
            PNG
          </button>
        </span>
      </header>
      {failed ? (
        <p className="flow-result-error">{nodeResult.error}</p>
      ) : (
        <div className="flow-result-body">{body(node, params, rows, fields, nodeResult)}</div>
      )}
      {imageError ? <p className="flow-result-error">{imageError}</p> : null}
    </article>
  );

  // Renders the card's chart offscreen and downloads it as a PNG; maps fetch
  // their tiles first, so a failed render is reported in the card.
  async function downloadImage() {
    setImageError(null);
    try {
      const canvas = await renderExportCanvas({
        chartType,
        rows,
        fields,
        config: params.config || {},
        background
      });
      if (canvas) downloadCanvasAsPng(canvas, `${slugify(title)}.png`);
    } catch (error) {
      setImageError(error && error.message ? error.message : 'Could not render the image.');
    }
  }
}

function body(node, params, rows, fields, nodeResult) {
  if (!rows.length) return <p className="viz-empty">Run the workflow to see this output.</p>;
  if (node.type === 'chart') {
    return (
      <Viz
        chartType={params.chartType || 'table'}
        rows={rows}
        fields={fields}
        config={params.config || {}}
      />
    );
  }
  if (nodeResult && nodeResult.chartType) {
    return (
      <Viz
        chartType={nodeResult.chartType}
        rows={rows}
        fields={fields}
        config={{}}
        viz={nodeResult.viz}
      />
    );
  }
  return <Viz chartType="table" rows={rows} fields={fields} config={{ labels: params.labels }} />;
}

function statusText(nodeResult, failed) {
  if (!nodeResult) return 'not run';
  if (failed) return 'failed';
  return `${nodeResult.count != null ? nodeResult.count : nodeResult.rows.length} rows`;
}
